import cx from "classnames";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ReactNode, useEffect } from "react";

import { AnimatedBackground } from "components/AnimatedBackground";

interface ModalProps {
  /** Header */
  header?: ReactNode;
  /** Body */
  children: ReactNode;
  /** Footer */
  footer?: ReactNode;
  /** Open/hide */
  open?: boolean;
  /** On close callback */
  onClose?: () => void;
  /** Specify the size */
  size?: string;
  /** Whether the modal is full screen */
  fullScreen?: boolean;
  /**
   * Where the panel sits from `desk` up. `dialog` (the default) centres it over
   * a dimmed page. `docked` parks it in the bottom-right corner as a messenger
   * panel and leaves the page undimmed behind it. Below `desk` the two are the
   * same bottom sheet.
   */
  desktopPlacement?: "dialog" | "docked";
  /** Modal class name */
  modalClassName?: string;
  /** Header class name */
  headerClassName?: string;
  /** Body class name */
  bodyClassName?: string;
  /** Footer class name */
  footerClassName?: string;
  /** Animation duration in seconds */
  animationDuration?: number;
  /** Use animated background */
  useAnimatedBackground?: boolean;
}

/**
 * Atomic component - Modal
 */
export const Modal = ({
  header,
  children,
  footer,
  open,
  onClose,
  size,
  fullScreen,
  desktopPlacement = "dialog",
  modalClassName = "rounded-t-xl",
  headerClassName,
  bodyClassName,
  footerClassName,
  animationDuration = 0.3,
  useAnimatedBackground = false,
}: ModalProps) => {
  const prefersReducedMotion = useReducedMotion();
  const isDocked = desktopPlacement === "docked";
  // Add overflow-hidden to #root when the modal is open
  // So the modal is the only thing that can be scrolled
  // Note: We target #root instead of body because body has overflow:hidden
  // for mobile browser viewport fixes
  useEffect(() => {
    const root = document.getElementById("root");
    if (open) {
      root?.classList.add("overflow-hidden");
    } else {
      root?.classList.remove("overflow-hidden");
    }

    return () => {
      root?.classList.remove("overflow-hidden");
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={cx(
              "fixed inset-0 z-50 bg-black/15",
              // A docked panel sits beside the page rather than over it, so the
              // page stays legible; the layer is kept for click-to-dismiss.
              isDocked && "desk:bg-transparent",
            )}
            onClick={onClose}
            data-testid="div-dismiss-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0 : animationDuration * 0.67,
              ease: "easeInOut",
            }}
          />
          {/* Positioning lives on this wrapper rather than on the panel so the
              same panel can be a bottom sheet on a phone and either a centred
              dialog or a bottom-right docked panel on desktop. It must not eat
              clicks meant for the dismiss overlay behind it, hence
              pointer-events-none here / auto on the panel. */}
          <div
            className={cx(
              "pointer-events-none fixed inset-0 z-50 flex flex-col items-center justify-end",
              isDocked
                ? "desk:items-end desk:justify-end desk:p-6"
                : "desk:justify-center desk:p-8",
            )}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              className={cx(
                "Modal pointer-events-auto flex max-h-[calc(100%_-_env(safe-area-inset-top)_-_12px)] w-full flex-col desk:max-h-full desk:min-h-0 desk:w-[440px] desk:!transform-none desk:overflow-hidden desk:rounded-xl desk:shadow-xl sm:w-[390px]",
                !useAnimatedBackground && "bg-white",
                useAnimatedBackground && "overflow-hidden",
                size && size,
                modalClassName,
              )}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0 : animationDuration,
                ease: "easeInOut",
              }}
            >
              {useAnimatedBackground ? (
                <AnimatedBackground
                  mode="light-prominent"
                  className="flex h-full grow flex-col overflow-auto overscroll-contain"
                >
                  <div className={cx("flex-none", headerClassName)}>{header}</div>
                  <div className={cx("grow overflow-auto overscroll-contain", bodyClassName)}>
                    {children}
                  </div>
                  <div
                    className={cx(
                      "flex-none",
                      fullScreen ? "pb-0" : "pb-[env(safe-area-inset-bottom)]",
                      footerClassName,
                    )}
                  >
                    {footer}
                  </div>
                </AnimatedBackground>
              ) : (
                <>
                  <div className={cx("flex-none", headerClassName)}>{header}</div>
                  <div className={cx("grow overflow-auto overscroll-contain", bodyClassName)}>
                    {children}
                  </div>
                  <div
                    className={cx(
                      "flex-none",
                      fullScreen ? "pb-0" : "pb-[env(safe-area-inset-bottom)]",
                      footerClassName,
                    )}
                  >
                    {footer}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
